import { useEffect, useMemo, useState } from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import type { AuthUser } from "@/auth/helloCashflowDomain/auth.types";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "SimpleProtectedRoute",
);
// This hook only distinguishes "admin required" from "no admin claim required". Reuse a
// shared array instance so callers can pass a stable reference when they mean "admin".
const ADMIN_REQUIRED_CLAIMS = ["admin"];

interface UseAdminClaimCheckArgs {
  adminE2eBypassEnabled: boolean;
  claimCheckAttempt: number;
  requiredClaims: string[];
  user: AuthUser | null;
}

interface UseAdminClaimCheckResult {
  claimCheckFailed: boolean;
  hasAdminClaim: boolean;
  isCheckingClaims: boolean;
  requiresAdminClaim: boolean;
}

interface AdminClaimCheckState {
  claimCheckFailed: boolean;
  hasAdminClaim: boolean;
  key: string;
}

/**
 * Reject malformed auth objects before the admin shell starts token work.
 *
 * @remarks
 * The global claim check depends on the live Firebase `User` instance. Spread
 * copies look similar in logs but lose prototype helpers such as
 * `getIdTokenResult()`, which would otherwise turn into an opaque runtime
 * failure after the shell has already committed to the access-check path.
 *
 * @see apps/admin/src/lib/firebase.ts
 */
export const assertClaimCheckUserShape = (user: AuthUser): void => {
  if (typeof user.getIdTokenResult !== "function") {
    throw new TypeError(
      "User object is missing getIdTokenResult; expected a live Firebase user instance.",
    );
  }
};

/**
 * Checks whether the current Firebase user has the required admin custom claim.
 *
 * @remarks
 * Today this hook only enforces the `"admin"` custom claim. `requiredClaims` is
 * treated as a *signal* for whether admin-claim enforcement should run; other
 * claim strings are ignored (do not assume generic RBAC here).
 *
 * We check the cached token first, then force-refresh once if the admin claim is
 * missing. This is intentional: Firebase custom claims can be stale until a
 * refreshed ID token is issued.
 *
 * In Admin E2E bypass mode, we skip the check entirely and treat the user as
 * having the admin claim so routes can render without RBAC setup.
 *
 * @see apps/admin/src/components/auth/SimpleProtectedRoute.tsx
 * @see apps/admin/src/utils/featureFlags.ts
 * @see docs/LOGGING.md
 */
export const useAdminClaimCheck = ({
  adminE2eBypassEnabled,
  claimCheckAttempt,
  requiredClaims,
  user,
}: UseAdminClaimCheckArgs): UseAdminClaimCheckResult => {
  const requiresAdminClaim = useMemo(
    () => requiredClaims.includes("admin"),
    [requiredClaims],
  );
  const requiredClaimsForLogs = useMemo(
    () => (requiresAdminClaim ? ADMIN_REQUIRED_CLAIMS : []),
    [requiresAdminClaim],
  );
  const bypassesClaimCheck = adminE2eBypassEnabled || !requiresAdminClaim;
  const claimCheckKey = user ? `${user.uid}:${claimCheckAttempt}` : null;
  const [claimCheckState, setClaimCheckState] =
    useState<AdminClaimCheckState | null>(null);

  // Depend on the semantic "admin required" boolean rather than the caller's array identity.
  // Route/search rerenders can rebuild `requiredClaims` without changing the access contract, and
  // rechecking Firebase tokens on those renders just creates auth churn without improving security.
  useEffect(() => {
    if (bypassesClaimCheck || !user || !claimCheckKey) {
      return;
    }

    let cancelled = false;

    const checkAdminClaims = async () => {
      try {
        assertClaimCheckUserShape(user);
        const cachedTokenResult = await user.getIdTokenResult();
        let tokenResult = cachedTokenResult;
        let tokenWasForceRefreshed = false;
        let isAdmin = !!cachedTokenResult.claims["admin"];

        logger.debug("Initial admin-claim check (cached token)", {
          operation: "claims_check_cached",
          userIdPresent: Boolean(user.uid),
          hasAdminClaim: isAdmin,
        });

        if (!isAdmin) {
          // Custom claims are not guaranteed to be present on the cached token immediately
          // after a claim is added/changed, so we do a single forced refresh.
          logger.warning("No admin claim found; forcing token refresh", {
            operation: "claims_force_refresh",
            userIdPresent: Boolean(user.uid),
          });
          tokenResult = await user.getIdTokenResult(true);
          tokenWasForceRefreshed = true;
          isAdmin = !!tokenResult.claims["admin"];
        }

        if (cancelled) {
          return;
        }

        setClaimCheckState((previousState) => {
          if (
            previousState?.claimCheckFailed === false &&
            previousState.hasAdminClaim === isAdmin &&
            previousState.key === claimCheckKey
          ) {
            return previousState;
          }

          return {
            claimCheckFailed: false,
            hasAdminClaim: isAdmin,
            key: claimCheckKey,
          };
        });

        if (!isAdmin) {
          logger.warn("Access denied: missing required admin claim", {
            operation: "claims_access_denied",
            userIdPresent: Boolean(user.uid),
            requiredClaims: requiredClaimsForLogs,
            claimKeys: Object.keys(tokenResult.claims),
            tokenWasForceRefreshed,
          });
        } else {
          logger.debug("Access granted (admin claim present)", {
            operation: "claims_access_granted",
            userIdPresent: Boolean(user.uid),
          });
        }
      } catch (error) {
        logger.error(
          "Error checking admin claims",
          error instanceof Error ? error : undefined,
          { operation: "claims_check_failed" },
        );
        if (!cancelled) {
          setClaimCheckState((previousState) => {
            if (
              previousState?.claimCheckFailed === true &&
              previousState.hasAdminClaim === false &&
              previousState.key === claimCheckKey
            ) {
              return previousState;
            }

            return {
              claimCheckFailed: true,
              hasAdminClaim: false,
              key: claimCheckKey,
            };
          });
        }
      }
    };

    void checkAdminClaims();

    return () => {
      cancelled = true;
    };
  }, [
    adminE2eBypassEnabled,
    bypassesClaimCheck,
    claimCheckKey,
    claimCheckAttempt,
    requiredClaimsForLogs,
    requiresAdminClaim,
    user,
  ]);

  if (bypassesClaimCheck) {
    return {
      claimCheckFailed: false,
      hasAdminClaim: true,
      isCheckingClaims: false,
      requiresAdminClaim,
    };
  }

  if (!user || claimCheckKey === null) {
    return {
      claimCheckFailed: false,
      hasAdminClaim: false,
      isCheckingClaims: false,
      requiresAdminClaim,
    };
  }

  const isCheckingClaims = claimCheckState?.key !== claimCheckKey;
  const hasAdminClaim =
    claimCheckState?.key === claimCheckKey
      ? claimCheckState.hasAdminClaim
      : false;
  const claimCheckFailed =
    claimCheckState?.key === claimCheckKey
      ? claimCheckState.claimCheckFailed
      : false;

  return {
    claimCheckFailed,
    hasAdminClaim,
    isCheckingClaims,
    requiresAdminClaim,
  };
};
